import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { listDoorsForSchool, createDoor, deleteDoor } from '../../api/hikvision'
import { lastPollLine } from '../../lib/lastPollLabel'
import { sharedBoolean } from '../../lib/schoolEquipmentIdentity'
import AddHikvisionDeviceModal from '../AddHikvisionDeviceModal'
import {
  AddDeviceLink,
  availabilityAccent,
  DeviceRowCard,
  EquipmentEmpty,
  IconAction,
  NavPageLink,
  SectionFrame,
  StatusDot,
} from './schoolEquipmentChrome'

const isAdminOrEngineer = (role) => {
  const r = typeof role === 'string' ? role.trim().toLowerCase() : ''
  return r === 'admin' || r === 'engineer'
}

const pluralizeStations = (count, t) => {
  if (count === 1) return t('schoolDashboard.panicStationCountOne')
  if (count >= 2 && count <= 4) return t('schoolDashboard.panicStationCountFew', { count })
  return t('schoolDashboard.panicStationCountMany', { count })
}

const connectivity = (station, t) => {
  if (station.enabled === false) {
    return { tone: 'muted', label: t('equipment.state.polling_disabled') }
  }
  if (station.status === true) {
    return { tone: 'ok', label: t('equipment.state.available') }
  }
  if (station.status === false) {
    return { tone: 'critical', label: t('equipment.state.unavailable') }
  }
  return { tone: 'muted', label: t('equipment.state.no_data') }
}

const HikvisionPanicSection = ({ objectIp, userRole }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const canManage = isAdminOrEngineer(userRole)

  const [stations, setStations] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState(null)
  const [deletingId, setDeletingId] = useState(null)

  const fetchStations = async () => {
    if (!objectIp) return
    setIsLoading(true)
    try {
      const data = await listDoorsForSchool(objectIp)
      setStations(Array.isArray(data) ? data.filter((d) => d.device_type === 'panic') : [])
      setError(null)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t('schoolDashboard.panicLoadError'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchStations()
    const interval = setInterval(fetchStations, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [objectIp])

  const onlineCount = useMemo(
    () => stations.filter((s) => s.enabled !== false && s.status === true).length,
    [stations]
  )

  const sharedVideo = sharedBoolean(stations, 'video_enabled')

  const subtitle = useMemo(() => {
    if (stations.length === 0) return t('schoolDashboard.panicSectionEmpty')
    const parts = [pluralizeStations(stations.length, t)]
    if (onlineCount === 1) parts.push('1 доступен')
    else if (onlineCount > 0) parts.push(`${onlineCount} доступны`)
    if (sharedVideo === true) {
      parts.push(`${t('schoolDashboard.panicCardVideo')}: ${t('schoolDashboard.panicVideoOn')}`)
    } else if (sharedVideo === false) {
      parts.push(`${t('schoolDashboard.panicCardVideo')}: ${t('schoolDashboard.panicVideoOff')}`)
    }
    parts.push(t('schoolDashboard.panicCardMonitoringHint'))
    return parts.join(' · ')
  }, [stations.length, onlineCount, sharedVideo, t])

  const handleCreate = async (data) => {
    setIsCreating(true)
    setCreateError(null)
    try {
      await createDoor(objectIp, { ...data, device_type: 'panic' })
      setIsAddModalOpen(false)
      fetchStations()
    } catch (err) {
      setCreateError(err?.response?.data?.message || err?.message || t('common.error'))
    } finally {
      setIsCreating(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm(t('schoolDashboard.panicDeleteConfirm'))) return
    setDeletingId(id)
    try {
      await deleteDoor(id)
      fetchStations()
    } catch (err) {
      alert(err?.response?.data?.message || t('common.error'))
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <SectionFrame
      eyebrow={t('schoolDashboard.panicSectionBadge')}
      title={t('schoolDashboard.panicSectionTitle')}
      subtitle={subtitle}
      actions={
        canManage ? (
          <AddDeviceLink
            onClick={() => setIsAddModalOpen(true)}
            label={t('schoolDashboard.panicAddDevice')}
          />
        ) : null
      }
      navLinks={
        <NavPageLink icon="notifications_active" onClick={() => navigate('/panic-incidents')}>
          {t('navigation.panic')}
        </NavPageLink>
      }
    >
      {isLoading && (
        <div className="flex h-24 items-center justify-center text-sm text-muted-300">
          <span className="material-icons mr-2 animate-spin text-brand-300">progress_activity</span>
          {t('schoolDashboard.panicLoading')}
        </div>
      )}

      {!isLoading && error && (
        <div className="rounded-2xl bg-critical/10 px-5 py-4 text-sm text-critical">{error}</div>
      )}

      {!isLoading && !error && stations.length === 0 && (
        <EquipmentEmpty
          hint={t('schoolDashboard.panicEmptyHint')}
          canManage={canManage}
          onAdd={() => setIsAddModalOpen(true)}
          addLabel={t('schoolDashboard.panicAddDevice')}
        />
      )}

      {!isLoading && !error && stations.length > 0 && (
        <div className="flex flex-col gap-2">
          {stations.map((station) => {
            const state = connectivity(station, t)
            const poll = station.status === false ? lastPollLine(station.scrapedAt) : null
            const extra = [
              sharedVideo == null
                ? `${t('schoolDashboard.panicCardVideo')}: ${
                    station.video_enabled
                      ? t('schoolDashboard.panicVideoOn')
                      : t('schoolDashboard.panicVideoOff')
                  }`
                : null,
              poll,
            ]
              .filter(Boolean)
              .join(' · ') || null

            return (
              <DeviceRowCard
                key={station.id}
                icon="crisis_alert"
                name={station.name || `#${station.id}`}
                address={station.target}
                extra={extra}
                status={<StatusDot tone={state.tone} label={state.label} />}
                accent={availabilityAccent(state.tone)}
                onOpen={() =>
                  navigate(`/schools/${encodeURIComponent(objectIp)}/panic/${station.id}`)
                }
                hoverActions={
                  canManage ? (
                    <IconAction
                      title={t('common.delete')}
                      onClick={() => handleDelete(station.id)}
                      busy={deletingId === station.id}
                      icon="delete"
                      tone="critical"
                    />
                  ) : null
                }
              />
            )
          })}
        </div>
      )}

      <AddHikvisionDeviceModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleCreate}
        isLoading={isCreating}
        error={createError}
        deviceType="panic"
      />
    </SectionFrame>
  )
}

export default HikvisionPanicSection
